<template>
  <div class="bg-white dark:bg-gray-800/50 rounded-lg shadow overflow-hidden">
    <div class="px-3 sm:px-4 py-2 sm:py-3 border-b border-gray-200 dark:border-gray-700">
      <div class="flex items-center gap-2">
        <div class="p-1.5 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
          <svg
            class="w-5 h-5 text-indigo-600 dark:text-indigo-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
            />
          </svg>
        </div>
        <div>
          <h2 class="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
            Configuración de Impresión
          </h2>
          <p class="text-xs text-gray-600 dark:text-gray-400">
            Configura tus impresoras y tickets térmicos
          </p>
        </div>
      </div>
    </div>

    <div class="p-3 sm:p-4 space-y-3">
      <div
        v-if="platformFeatures.cloudOnly.menuManagement && !isPosOnlyMode"
        class="rounded-lg p-4"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-3 flex-1 min-w-0">
            <div class="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
              <svg
                class="w-6 h-6 text-purple-600 dark:text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-base font-bold text-gray-900 dark:text-white">
                Impresión Avanzada
              </h3>
              <p class="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                Activa el sistema multi-impresoras con routing por categoría y configuración individual
              </p>
            </div>
          </div>

          <button
            :disabled="isSaving"
            :class="[
              'relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-600 focus:ring-offset-2 dark:focus:ring-offset-gray-800',
              advancedPrintingEnabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600',
              isSaving ? 'opacity-50 cursor-not-allowed' : ''
            ]"
            @click="restaurantSettings.toggleAdvancedPrinting"
          >
            <span
              :class="[
                'pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
                advancedPrintingEnabled ? 'translate-x-5' : 'translate-x-0'
              ]"
            />
          </button>
        </div>

        <div
          v-if="advancedPrintingEnabled"
          class="mt-3 pt-3 border-t border-purple-200 dark:border-purple-800"
        >
          <router-link
            to="/printers"
            class="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            Configurar Impresoras
          </router-link>
        </div>
      </div>

      <div
        v-if="showKitchen && !advancedPrintingEnabled"
        class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div class="p-1.5 sm:p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex-shrink-0">
              <svg
                class="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 dark:text-indigo-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                {{ t('app.subscription.settings.kitchen_print.title') }}
              </h3>
              <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                {{ t('app.subscription.settings.kitchen_print.description') }}
              </p>
            </div>
          </div>
          <button
            :disabled="isSaving"
            :class="[
              'relative inline-flex h-6 w-11 sm:h-7 sm:w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2 dark:focus:ring-offset-gray-800',
              kitchenPrintEnabled ? 'bg-indigo-600' : 'bg-gray-300 dark:bg-gray-600',
              isSaving ? 'opacity-50 cursor-not-allowed' : ''
            ]"
            @click="restaurantSettings.toggleKitchenPrint"
          >
            <span
              :class="[
                'pointer-events-none inline-block h-5 w-5 sm:h-6 sm:w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
                kitchenPrintEnabled ? 'translate-x-5' : 'translate-x-0'
              ]"
            />
          </button>
        </div>
      </div>

      <transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div
          v-if="showKitchen && kitchenPrintEnabled && !advancedPrintingEnabled"
          class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-center gap-2 mb-3">
            <div class="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
              <svg
                class="w-5 h-5 sm:w-6 sm:h-6 text-purple-600 dark:text-purple-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                {{ t('app.subscription.settings.paper_width.title') }}
              </h3>
              <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                {{ t('app.subscription.settings.paper_width.description') }}
              </p>
            </div>
          </div>

          <PaperWidthOptions
            name="kitchen-paper-width"
            :options="kitchenPaperWidthOptions"
            :current-width="currentPaperWidth"
            :is-saving="isSaving"
            accent="indigo"
            @select="restaurantSettings.setPaperWidth"
          />
        </div>
      </transition>

      <div
        v-if="!advancedPrintingEnabled"
        class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3"
      >
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div class="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900/30 rounded-lg flex-shrink-0">
              <svg
                class="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                {{ t('app.subscription.settings.customer_print.title') }}
              </h3>
              <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                {{ t('app.subscription.settings.customer_print.description') }}
              </p>
            </div>
          </div>
          <button
            :disabled="isSaving"
            :class="[
              'relative inline-flex h-6 w-11 sm:h-7 sm:w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-600 focus:ring-offset-2 dark:focus:ring-offset-gray-800',
              customerPrintEnabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600',
              isSaving ? 'opacity-50 cursor-not-allowed' : ''
            ]"
            @click="restaurantSettings.toggleCustomerPrint"
          >
            <span
              :class="[
                'pointer-events-none inline-block h-5 w-5 sm:h-6 sm:w-6 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out',
                customerPrintEnabled ? 'translate-x-5' : 'translate-x-0'
              ]"
            />
          </button>
        </div>
      </div>

      <transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div
          v-if="customerPrintEnabled && !advancedPrintingEnabled"
          class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700"
        >
          <div class="flex items-center gap-2 mb-3">
            <div class="p-1.5 sm:p-2 bg-green-100 dark:bg-green-900/30 rounded-lg flex-shrink-0">
              <svg
                class="w-5 h-5 sm:w-6 sm:h-6 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                {{ t('app.subscription.settings.customer_paper_width.title') }}
              </h3>
              <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                {{ t('app.subscription.settings.customer_paper_width.description') }}
              </p>
            </div>
          </div>

          <PaperWidthOptions
            name="customer-paper-width"
            :options="customerPaperWidthOptions"
            :current-width="currentCustomerPaperWidth"
            :is-saving="isSaving"
            accent="green"
            @select="restaurantSettings.setCustomerPaperWidth"
          />
        </div>
      </transition>

      <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-3">
        <div class="flex items-center justify-between gap-3">
          <div class="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div class="p-1.5 sm:p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex-shrink-0">
              <svg
                class="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <h3 class="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">
                Probar Impresión
              </h3>
              <p class="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                Prueba tus tickets de cocina y cliente con datos de ejemplo
              </p>
            </div>
          </div>
          <router-link
            to="/print-test"
            class="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
              />
            </svg>
            <span class="hidden sm:inline">Probar Impresión</span>
            <span class="sm:hidden">Probar</span>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useOperationMode } from '@/composables/useOperationMode'
import { platformFeatures } from '@/utils/platform'
import PaperWidthOptions from '@/components/configuration/PaperWidthOptions.vue'
import type { useRestaurantSettings } from '@/composables/useRestaurantSettings'

const props = defineProps<{
  restaurantSettings: ReturnType<typeof useRestaurantSettings>
}>()

const { t } = useI18n()
const restaurantSettings = props.restaurantSettings
const { showKitchen, isPosOnlyMode } = useOperationMode()

const isSaving = computed(() => restaurantSettings.savingSettings.value)
const advancedPrintingEnabled = computed(() => restaurantSettings.advancedPrintingEnabled.value)
const kitchenPrintEnabled = computed(() => restaurantSettings.kitchenPrintEnabled.value)
const customerPrintEnabled = computed(() => restaurantSettings.customerPrintEnabled.value)
const currentPaperWidth = computed(() => restaurantSettings.paperWidth.value)
const currentCustomerPaperWidth = computed(() => restaurantSettings.customerPaperWidth.value)

const kitchenPaperWidthOptions = computed(() => [
  { value: 58, label: '58mm', description: t('app.subscription.settings.paper_width.compact') },
  { value: 80, label: '80mm', description: t('app.subscription.settings.paper_width.standard') }
])

const customerPaperWidthOptions = computed(() => [
  { value: 58, label: '58mm', description: t('app.subscription.settings.customer_paper_width.compact') },
  { value: 80, label: '80mm', description: t('app.subscription.settings.customer_paper_width.standard') }
])
</script>
